'use client';

import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import { localizeUrl, pickButton } from '@/lib/homepage-mappers';
import { getStrapiMediaUrl } from '@/lib/strapi-media-url';
import type { PreFooterItem, PreFooterZone } from '@/types/homepage';

interface AboutPreFooterSectionProps {
  zone?: PreFooterZone;
  locale?: string;
}

const FALLBACK_ITEMS = [
  {
    heading: 'Meet the Visionaries',
    bgClassName: 'bg-[#ededed]',
    logoSrc: '/images/wonder_lg.svg',
    buttonText: 'Leadership',
    buttonHref: '/leadership',
    variant: 'light' as const,
  },
  {
    heading: 'Built on Precision. Powered by Scale',
    bgClassName: 'bg-[#f0182b]',
    logoSrc: '/images/wonder_lg.svg',
    buttonText: 'Manufacturing Facilities',
    buttonHref: '/our-plants',
    variant: 'accent' as const,
  },
];

function resolvePanelVariant(
  item: PreFooterItem | undefined,
  fallback: (typeof FALLBACK_ITEMS)[number],
): 'light' | 'accent' {
  const usesWhiteButton = Boolean(item?.buttonWhite?.length && !item?.buttonDark?.length);
  const usesDarkButton = Boolean(item?.buttonDark?.length && !item?.buttonWhite?.length);

  if (usesWhiteButton) return 'accent';
  if (usesDarkButton) return 'light';
  return fallback.variant;
}

function resolveLogoSrc(item: PreFooterItem | undefined, fallbackLogo: string): string {
  return (
    getStrapiMediaUrl(item?.image, 'medium') ??
    getStrapiMediaUrl(item?.bgImage, 'medium') ??
    fallbackLogo
  );
}

function resolveItem(
  item: PreFooterItem | undefined,
  fallback: (typeof FALLBACK_ITEMS)[number],
  locale: string,
) {
  const button =
    pickButton(item?.buttonDark, 'Solid') ??
    item?.buttonDark?.[0] ??
    pickButton(item?.buttonWhite, 'Solid') ??
    item?.buttonWhite?.[0];

  const bgColor = item?.bgColor?.trim();
  const bgStyle = bgColor ? { backgroundColor: bgColor } : undefined;
  const bgClassName = bgStyle ? '' : fallback.bgClassName;

  const variant = resolvePanelVariant(item, fallback);

  return {
    heading: item?.heading?.trim() || fallback.heading,
    bgClassName,
    bgStyle,
    logoSrc: resolveLogoSrc(item, fallback.logoSrc),
    buttonText: button?.buttonText || fallback.buttonText,
    buttonHref: localizeUrl(button?.buttonUrl, locale) ?? fallback.buttonHref,
    buttonLinkProps: getCmsButtonLinkProps(button),
    variant,
  };
}

export default function AboutPreFooterSection({ zone, locale = 'en' }: AboutPreFooterSectionProps) {
  const items = zone?.item?.length
    ? zone.item.map((item, index) =>
        resolveItem(item, FALLBACK_ITEMS[index] ?? FALLBACK_ITEMS[0], locale),
      )
    : FALLBACK_ITEMS.map((fallback) => resolveItem(undefined, fallback, locale));

  return (
    <section className="overflow-hidden" aria-label="About page quick links">
      <div className="grid grid-cols-1 md:grid-cols-2">
        {items.map((item, index) => {
          const isLightPanel = item.variant === 'light';

          return (
            <div
              key={item.heading}
              className={`relative min-h-[200px] overflow-hidden md:min-h-[220px] ${item.bgClassName} ${
                isLightPanel ? 'order-2 md:order-1' : 'order-1 md:order-2'
              }`}
              style={item.bgStyle}
            >
              <div
                className={`pointer-events-none absolute right-0 top-1/2 hidden h-[210px] w-[360px] -translate-y-1/2 bg-contain bg-center bg-no-repeat md:block ${
                  isLightPanel ? 'opacity-[0.08]' : 'opacity-[0.15]'
                }`}
                style={{ backgroundImage: `url('${item.logoSrc}')` }}
                aria-hidden="true"
              />
              <div className="relative z-[1] flex min-h-[200px] flex-col justify-center px-5 py-8 sm:px-7 md:min-h-[220px] md:px-10">
                <div className={index === 0 ? 'max-w-[260px]' : 'max-w-[300px]'}>
                  <h3
                    className={`text-xl font-semibold leading-[1.1] tracking-tight sm:text-2xl md:text-[28px] ${
                      isLightPanel ? 'text-[#111111]' : 'text-white'
                    }`}
                  >
                    {item.heading}
                  </h3>
                  <div className="mt-4 sm:mt-5">
                    <SwapTextButton
                      text={item.buttonText}
                      href={item.buttonHref}
                      {...item.buttonLinkProps}
                      tone={isLightPanel ? undefined : 'light'}
                      showArrow
                      className={
                        isLightPanel
                          ? 'min-h-[44px] rounded-[10px] px-4 py-2.5 text-sm font-medium shadow-[0_12px_28px_rgba(240,24,43,0.24)]'
                          : 'min-h-[44px] rounded-[10px] px-4 py-2.5 text-sm font-medium shadow-none'
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
